import type { Theme, ThemeOptions } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const INPUT_FG = '#1E2A3A';

/** Layout + indicator styling for `Autocomplete` (field chrome from `Input` / `SmInputBase-root`). */
export function autocompleteThemeComponents(theme: Theme): ThemeOptions['components'] {
  const iconColor = theme.palette.action.active;
  const plainIconColor = INPUT_FG;
  const selectedOptionBg = theme.palette.primary.light;

  return {
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <KeyboardArrowDownIcon />,
      },
      styleOverrides: {
        paper: {
          '&.SmSelectMenu-paper': {
            backgroundColor: theme.palette.common.white,
            borderRadius: '10px',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none'
          }
        },
        listbox: {
          '&.SmSelectMenu-list': {
            backgroundColor: theme.palette.common.white,
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              backgroundColor: `${selectedOptionBg} !important`
            },
            '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
              backgroundColor: `${selectedOptionBg} !important`
            },
            '& .MuiAutocomplete-option[aria-selected="true"].Mui-focusVisible': {
              backgroundColor: `${selectedOptionBg} !important`
            }
          }
        },
        root: {
          '&.SmAutocomplete-root': {
            display: 'block',
            width: '100%',
            '& .SmInputBase-root': {
              position: 'relative',
              '& .MuiInputBase-input': {
                paddingInlineEnd: '45px'
              },
              '&:has(.SmInputAdornment-combinedEnd) .MuiInputBase-input': {
                paddingInlineEnd: '76px'
              },
              '& .MuiInputAdornment-positionEnd': {
                position: 'absolute',
                insetInlineEnd: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                margin: 0,
                maxHeight: 'none',
                height: 'auto',
                gap: 4,
                color: iconColor
              },
              '& .SmInputAdornment-combinedEnd': {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                // MUI's own .MuiAutocomplete-endAdornment is hardcoded `position: absolute; right: 0`
                // ("to support wrapping tags"), which pins it to the edge regardless of flex layout —
                // it never actually sits next to our custom icon. Put it back in normal flow here so
                // `gap` creates real spacing instead of the two overlapping.
                '& .MuiAutocomplete-endAdornment': {
                  position: 'static',
                  top: 'auto',
                  right: 'auto',
                  transform: 'none'
                }
              },
              '& .SmInputAdornment-customEndIcon': {
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                '& .MuiSvgIcon-root': {
                  fontSize: 18,
                  width: 18,
                  height: 18
                }
              },
              '& .SmInputAdornment-loaderSlot': {
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0
              },
              '& .SmInputAdornment-loaderSlot-hidden': {
                visibility: 'hidden'
              },
              '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                margin: 0,
                padding: 0,
                color: iconColor,
                backgroundColor: 'transparent',
                borderRadius: 0,
                transition: 'none'
              }
            },
            // MUI v5 has no `MuiAutocomplete-multiple` class, so we set our own (see Autocomplete.tsx).
            // Single line: `FittedTags` shows what fits plus a `+N` counter. The end indicators are
            // absolutely positioned, so their space is reserved on the field, not the input.
            // `gap` and the input's `minWidth` are mirrored in FittedTags.tsx.
            '&.SmAutocomplete-multiple .SmInputBase-root': {
              flexWrap: 'nowrap',
              overflow: 'hidden',
              gap: 4,
              paddingInlineEnd: '45px',
              '&:has(.SmInputAdornment-combinedEnd)': {
                paddingInlineEnd: '76px'
              },
              '& .MuiInputBase-input': {
                flex: '1 0 30px',
                width: 0,
                minWidth: 30,
                paddingInlineEnd: 0
              }
            },
            '&.SmAutocomplete-multiple .SmAutocomplete-tags': {
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              flex: '0 1 auto',
              minWidth: 0,
              overflow: 'hidden'
            },
            '&.SmAutocomplete-multiple .MuiAutocomplete-tag': {
              margin: 0,
              minWidth: 0,
              flexShrink: 1
            },
            '&.SmAutocomplete-multiple .SmAutocomplete-tagCounter': {
              flexShrink: 0
            },
            '&.SmAutocomplete-multiple .SmAutocomplete-tagsMeasure': {
              position: 'absolute',
              top: 0,
              insetInlineStart: 0,
              display: 'flex',
              gap: 4,
              visibility: 'hidden',
              pointerEvents: 'none',
              '& .MuiChip-root': {
                flexShrink: 0,
                maxWidth: 'none'
              }
            }
          },
          '&.SmAutocomplete-appearance-plain .SmInputBase-root': {
            '& .MuiInputAdornment-positionEnd': {
              color: plainIconColor
            },
            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
              color: `${plainIconColor} !important`,
              '& .MuiSvgIcon-root': {
                color: `${plainIconColor} !important`
              }
            },
            '&.Mui-disabled .MuiAutocomplete-popupIndicator, &.Mui-disabled .MuiAutocomplete-clearIndicator, & .Mui-disabled.MuiAutocomplete-popupIndicator, & .Mui-disabled.MuiAutocomplete-clearIndicator':
              {
                color: 'rgba(30, 42, 58, 0.4) !important',
                '& .MuiSvgIcon-root': {
                  color: 'rgba(30, 42, 58, 0.4) !important'
                }
              }
          }
        }
      }
    }
  };
}
